import Microgame from '../engine/Microgame.js';

export default class FanGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Ventile !`, en: `Fan!`, es: `¡Sopla!` },
            controls: { fr: `Tourne en rond avec ton doigt ou la souris`, en: `Make circles with your finger or mouse`, es: `Haz círculos con el dedo o el ratón` },
            objective: { fr: `Fais tourner les pales assez vite pour créer du vent`, en: `Spin the blades fast enough to create wind`, es: `Gira las aspas lo suficientemente rápido` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M12 12v3M12 9V5M9 12H5M19 12h-4"/></svg>`,
            actions: [
                { type: 'swipe', fromX: 50, fromY: 28, toX: 72, toY: 50, duration: 220 },
                { type: 'swipe', fromX: 72, fromY: 50, toX: 50, toY: 72, duration: 220 },
                { type: 'swipe', fromX: 50, fromY: 72, toX: 28, toY: 50, duration: 220 },
                { type: 'swipe', fromX: 28, fromY: 50, toX: 50, toY: 28, duration: 220 },
                { type: 'swipe', fromX: 50, fromY: 28, toX: 72, toY: 50, duration: 220 },
                { type: 'wait', duration: 360 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.fan = null;
        this.rotation = 0;
        this.totalRotation = 0;
        this.velocity = 0;
        this.isSwiping = false;
        this.lastX = 0;
        this.lastY = 0;

        // Wind particles
        this.particles = [];
        this.fanCenterX = 0;
        this.fanCenterY = 0;

        // Target: number of full circles to complete
        const speedMult = engine.speedMultiplier || 1.0;
        const targetCircles = Math.max(1.8, 3.5 - (speedMult - 1) * (1.7 / 3));
        this.targetRotation = targetCircles * 360;

        this.progressBar = null;
    }

    setup() {
        this.engine.ui.instruction.textContent = "BLOW!";

        // Fan Container
        this.fan = document.createElement('div');
        this.fan.style.width = '200px';
        this.fan.style.height = '200px';
        this.fan.style.position = 'absolute';
        this.fan.style.top = '45%';
        this.fan.style.left = '50%';
        this.fan.style.transform = 'translate(-50%, -50%)';
        this.container.appendChild(this.fan);

        // Blades
        for (let i = 0; i < 3; i++) {
            const blade = document.createElement('div');
            blade.style.position = 'absolute';
            blade.style.width = '20px';
            blade.style.height = '100px';
            blade.style.background = this.primaryColor;
            blade.style.boxShadow = `0 0 15px ${this.primaryColor}`;
            blade.style.borderRadius = '10px';
            blade.style.top = '50%';
            blade.style.left = '50%';
            blade.style.transformOrigin = '50% 0%';
            blade.style.transform = `translate(-50%, 0) rotate(${i * 120}deg)`;
            this.fan.appendChild(blade);
        }

        // Center Hub
        const hub = document.createElement('div');
        hub.style.position = 'absolute';
        hub.style.width = '30px';
        hub.style.height = '30px';
        hub.style.background = this.secondaryColor;
        hub.style.borderRadius = '50%';
        hub.style.top = '50%';
        hub.style.left = '50%';
        hub.style.transform = 'translate(-50%, -50%)';
        hub.style.zIndex = '2';
        this.fan.appendChild(hub);

        // Compute fan center in container coords
        const fanRect = this.fan.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        this.fanCenterX = fanRect.left + fanRect.width / 2 - containerRect.left;
        this.fanCenterY = fanRect.top + fanRect.height / 2 - containerRect.top;

        // Progress Bar
        const barContainer = document.createElement('div');
        barContainer.style.position = 'absolute';
        barContainer.style.bottom = '10%';
        barContainer.style.left = '50%';
        barContainer.style.transform = 'translateX(-50%)';
        barContainer.style.width = '70%';
        barContainer.style.height = '15px';
        barContainer.style.background = 'rgba(0, 0, 0, 0.5)';
        barContainer.style.border = `2px solid ${this.secondaryColor}`;
        barContainer.style.borderRadius = '10px';
        barContainer.style.overflow = 'hidden';
        barContainer.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        this.container.appendChild(barContainer);

        this.progressBar = document.createElement('div');
        this.progressBar.style.width = '0%';
        this.progressBar.style.height = '100%';
        this.progressBar.style.background = this.primaryColor;
        this.progressBar.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.progressBar.style.transition = 'width 0.1s';
        barContainer.appendChild(this.progressBar);

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    onDown(e) {
        if (!this.active) return;
        this.isSwiping = true;
        this.lastX = e.clientX;
        this.lastY = e.clientY;
    }

    onMove(e) {
        if (!this.active || !this.isSwiping) return;

        const dx = e.clientX - this.lastX;
        const dy = e.clientY - this.lastY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 1) {
            // Any movement generates "wind" force — generous cap for circular/lateral swipes
            const cappedDist = Math.min(dist, 80);
            const speedMult = this.engine.speedMultiplier || 1.0;
            this.velocity += cappedDist * 1.8 * speedMult;

            // Spawn wind particles from finger position toward the fan
            this.spawnWindParticles(e.clientX, e.clientY, dist);
        }

        this.lastX = e.clientX;
        this.lastY = e.clientY;
    }

    onUp() {
        this.isSwiping = false;
    }

    spawnWindParticles(fingerX, fingerY, moveDist) {
        const containerRect = this.container.getBoundingClientRect();
        const startX = fingerX - containerRect.left;
        const startY = fingerY - containerRect.top;

        // Direction from finger toward fan center
        const dirX = this.fanCenterX - startX;
        const dirY = this.fanCenterY - startY;
        const angle = Math.atan2(dirY, dirX);

        // Number of particles based on movement intensity
        const count = Math.min(Math.floor(moveDist / 8) + 1, 5);

        for (let i = 0; i < count; i++) {
            const particle = document.createElement('div');
            const size = Math.random() * 6 + 3;

            particle.style.position = 'absolute';
            particle.style.width = `${size}px`;
            particle.style.height = `${size}px`;
            particle.style.borderRadius = '50%';
            particle.style.background = this.primaryColor;
            particle.style.opacity = '0.7';
            particle.style.boxShadow = `0 0 ${size}px ${this.primaryColor}`;
            particle.style.pointerEvents = 'none';
            particle.style.zIndex = '5';

            // Slight random offset around finger position
            const offsetX = (Math.random() - 0.5) * 30;
            const offsetY = (Math.random() - 0.5) * 30;
            const x = startX + offsetX;
            const y = startY + offsetY;

            particle.style.left = `${x}px`;
            particle.style.top = `${y}px`;
            this.container.appendChild(particle);

            // Random slight angle variation
            const angleVar = angle + (Math.random() - 0.5) * 0.6;
            const speed = 200 + Math.random() * 150;

            this.particles.push({
                el: particle,
                x: x,
                y: y,
                vx: Math.cos(angleVar) * speed,
                vy: Math.sin(angleVar) * speed,
                life: 0.6 + Math.random() * 0.3,
                maxLife: 0.6 + Math.random() * 0.3
            });
        }
    }

    update(dt) {
        if (!this.active) return;
        const dtSec = dt / 1000;

        // Apply velocity to rotation
        const rotationStep = this.velocity * dtSec;
        this.rotation += rotationStep;

        if (this.velocity > 0) {
            this.totalRotation += Math.abs(rotationStep);
        }

        this.fan.style.transform = `translate(-50%, -50%) rotate(${this.rotation}deg)`;

        // Friction (adapted for dt)
        this.velocity *= Math.pow(0.96, dt / 16.66);

        // Update wind particles
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];
            p.x += p.vx * dtSec;
            p.y += p.vy * dtSec;
            p.life -= dtSec;

            if (p.life <= 0) {
                p.el.remove();
                this.particles.splice(i, 1);
            } else {
                const alpha = Math.max(0, p.life / p.maxLife) * 0.7;
                p.el.style.left = `${p.x}px`;
                p.el.style.top = `${p.y}px`;
                p.el.style.opacity = alpha;
            }
        }

        // Update progress bar
        const progress = Math.min(100, Math.max(0, (this.totalRotation / this.targetRotation) * 100));
        this.progressBar.style.width = `${progress}%`;

        // Win Condition
        if (this.totalRotation >= this.targetRotation) {
            this.win();
            this.fan.style.filter = 'blur(2px)';
            this.progressBar.style.width = '100%';
        }
    }

    cleanup() {
        super.cleanup();
        // Remove remaining particles
        for (const p of this.particles) {
            p.el.remove();
        }
        this.particles = [];
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
